import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { environment } from 'environments/environment';
import { Subscription, interval, of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';

interface VersionJson {
    appVersion: string;
}

/**
 * Version Checker Service
 * Periodically checks assets/version.json for version updates
 * and automatically reloads the page when a new version is detected
 */
@Injectable({
    providedIn: 'root',
})
export class VersionCheckerService {
    private readonly _http = inject(HttpClient);
    private readonly CHECK_INTERVAL = 5 * 60 * 1000; // Check every 5 minutes
    private readonly VERSION_FILE_PATH = 'assets/version.json';
    private readonly currentVersion: string = environment.version;
    private checkIntervalSubscription: Subscription | undefined;

    /**
     * Start checking for version updates
     */
    startVersionChecking(): void {
        // Check immediately on start
        this.checkVersion();

        // Then check periodically
        this.checkIntervalSubscription = interval(
            this.CHECK_INTERVAL
        ).subscribe(() => {
            this.checkVersion();
        });
    }

    /**
     * Stop checking for version updates
     */
    stopVersionChecking(): void {
        if (this.checkIntervalSubscription) {
            this.checkIntervalSubscription.unsubscribe();
        }
    }

    /**
     * Check if a new version is available
     */
    private checkVersion(): void {
        this._http
            .get<VersionJson>(this.VERSION_FILE_PATH, {
                headers: {
                    'Cache-Control': 'no-cache',
                    Pragma: 'no-cache',
                },
            })
            .pipe(
                tap((versionData) => {
                    const remoteVersion = this.extractVersion(
                        versionData.appVersion
                    );
                    const localVersion = this.extractVersion(
                        this.currentVersion
                    );

                    if (this.isNewVersion(remoteVersion, localVersion)) {
                        console.log(
                            `🔄 New version detected: ${remoteVersion} (current: ${localVersion}). Reloading page...`
                        );
                        this.reloadPage();
                    }
                }),
                catchError((error) => {
                    // Silently handle errors (version.json might not exist or network issues)
                    // Don't spam console with errors
                    if (error.status === 404) {
                        console.debug(
                            'ℹ️  version.json not found. This is normal on first deploy or if version checking is disabled.'
                        );
                    }
                    return of(null);
                })
            )
            .subscribe();
    }

    /**
     * Extract version number from version string
     * Handles formats like "1.0.0" or "1.0.0+20251030091500"
     */
    private extractVersion(version: string): string {
        if (!version) return '';
        // Extract version before the '+' if present (format: "1.0.0+timestamp")
        return version.split('+')[0].trim();
    }

    /**
     * Check if remote version is newer than local version
     */
    private isNewVersion(remoteVersion: string, localVersion: string): boolean {
        if (!remoteVersion || !localVersion) return false;
        if (remoteVersion === localVersion) return false;

        const remoteParts = remoteVersion.split('.').map(Number);
        const localParts = localVersion.split('.').map(Number);

        for (
            let i = 0;
            i < Math.max(remoteParts.length, localParts.length);
            i++
        ) {
            const remotePart = remoteParts[i] || 0;
            const localPart = localParts[i] || 0;

            if (remotePart > localPart) return true;
            if (remotePart < localPart) return false;
        }

        return false;
    }

    /**
     * Reload the page to get the new version
     */
    private reloadPage(): void {
        globalThis.location.reload();
    }
}
